<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-sm-6">
                <div class="search-box mr-2 mb-2 d-inline-block">
                  <div class="position-relative">
                    <input
                      type="text"
                      class="form-control"
                      v-model="search"
                      placeholder="Digite e pressione 'ENTER'"
                      @keyup.enter="doSearch"
                    />
                    <i class="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="float-sm-end d-flex">
                  <div class="text-sm-right">
                    <button
                      type="button"
                      class="btn btn-success btn-rounded mb-2 mr-2"
                      @click="openCreateModal"
                    >
                      <i class="mdi mdi-plus mr-1"></i> Novo Ingresso
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-centered table-nowrap table-hover">
                <thead>
                  <tr>
                    <th width="25%">Ingresso</th>
                    <th width="12%">Preço</th>
                    <th width="15%">Quantidade</th>
                    <th width="20%">Período</th>
                    <th width="10%">Status</th>
                    <th width="5%">#</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ticket in tickets" :key="ticket.id">
                    <td class="link" @click="editTicket(ticket)">
                      <div class="fw-medium">{{ ticket.name }}</div>
                      <small class="text-muted" v-if="ticket.description">{{ ticket.description }}</small>
                    </td>
                    <td class="link" @click="editTicket(ticket)">
                      {{ formatCurrency(ticket.amount_ticket) }}
                      <span v-if="ticket.type_ticket === 1" class="badge bg-info-subtle text-info ms-1">Gratuito</span>
                    </td>
                    <td class="link" @click="editTicket(ticket)">
                      <div>{{ ticket.qtd_sold }} / {{ ticket.qtd }}</div>
                      <small v-if="ticket.qtd_sold >= ticket.qtd" class="text-danger">
                        Esgotado
                      </small>
                    </td>
                    <td class="link" @click="editTicket(ticket)">
                      <div v-if="ticket.sale_start_date && ticket.sale_end_date" class="small">
                        <div>{{ formatDate(ticket.sale_start_date) }}</div>
                        <div class="text-muted">até {{ formatDate(ticket.sale_end_date) }}</div>
                      </div>
                      <div v-else class="text-muted">-</div>
                    </td>
                    <td>
                      <b-dropdown
                        size="sm"
                        style="min-width:100px"
                        :variant="ticket.status === 1 ? 'success' : 'danger'"
                      >
                        <template v-slot:button-content>
                          {{ ticket.status === 1 ? 'Ativo' : 'Inativo' }}
                          <i class="mdi mdi-chevron-down"></i>
                        </template>
                        <span>
                          <b-dropdown-item-button @click="toggleStatus(ticket)">
                            {{ ticket.status === 1 ? 'Inativar' : 'Ativar' }}
                          </b-dropdown-item-button>
                        </span>
                      </b-dropdown>
                    </td>
                    <td>
                      <button
                        type="button"
                        @click="deleteTicket(ticket)"
                        class="link btn btn-outline-danger btn-sm"
                      >
                        <i class="bx bx-trash"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="tickets.length === 0">
                    <td colspan="6" class="text-center text-muted py-4">
                      Nenhum ingresso cadastrado
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="pagination.current_page"
                      :total-rows="pagination.total"
                      :per-page="pagination.per_page"
                      @input="loadTickets"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEditing ? 'Editar ingresso' : 'Criar novo ingresso'"
      size="lg"
      hide-footer
      @hidden="resetForm"
      modal-class="ticket-modal"
    >
      <p class="text-muted mb-4">Configure o preço e disponibilidade do seu evento</p>

      <form @submit.prevent="saveTicket">
        <!-- Basic Information -->
        <div class="ticket-section">
          <div class="section-header">
            <div class="section-icon bg-primary-soft">
              <i class="bx bx-receipt text-primary"></i>
            </div>
            <h6 class="mb-0">Informações básicas</h6>
          </div>

          <div class="section-body">
            <div class="mb-3">
              <label class="form-label">Nome do ingresso <span class="text-danger">*</span></label>
              <input
                v-model="form.name"
                type="text"
                class="form-control"
                placeholder="Ex: Ingresso Geral, Passe VIP, Early Bird"
                :class="{ 'is-invalid': submitted && $v.form.name.$error }"
              />
              <div v-if="submitted && !$v.form.name.required" class="invalid-feedback">
                O campo nome do ingresso é obrigatório.
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Descrição <span class="text-muted">(opcional)</span></label>
              <textarea
                v-model="form.description"
                class="form-control"
                rows="3"
                placeholder="Adicione detalhes sobre o que está incluído neste ingresso"
              ></textarea>
              <small class="text-muted">Isso será mostrado aos participantes na página do evento</small>
            </div>

            <div>
              <label class="form-label d-block mb-2">Tipo de ingresso <span class="text-danger">*</span></label>
              <div class="btn-group ticket-type-toggle" role="group">
                <button
                  type="button"
                  class="btn"
                  :class="form.type_ticket === 1 ? 'btn-light active' : 'btn-outline-light'"
                  @click="form.type_ticket = 1"
                >
                  <i class="bx bx-gift me-1"></i> Gratuito
                </button>
                <button
                  type="button"
                  class="btn"
                  :class="form.type_ticket === 0 ? 'btn-primary active' : 'btn-outline-primary'"
                  @click="form.type_ticket = 0"
                >
                  <i class="bx bx-dollar me-1"></i> Pago
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Pricing -->
        <div v-if="form.type_ticket === 0" class="ticket-section">
          <div class="section-header">
            <div class="section-icon bg-success-soft">
              <i class="bx bx-dollar-circle text-success"></i>
            </div>
            <h6 class="mb-0">Preço</h6>
          </div>

          <div class="section-body">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Valor <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text">R$</span>
                  <input
                    v-model="form.amount_ticket"
                    v-money="moneyConfig"
                    type="text"
                    class="form-control"
                    placeholder="0,00"
                    :class="{ 'is-invalid': submitted && $v.form.amount_ticket.$error }"
                    @blur="form.amount_ticket = parseFloat(form.amount_ticket) || null"
                  />
                </div>
                <div v-if="submitted && !$v.form.amount_ticket.required" class="invalid-feedback d-block">
                  O campo valor é obrigatório para ingressos pagos.
                </div>
                <div v-if="submitted && !$v.form.amount_ticket.minValue" class="invalid-feedback d-block">
                  O valor deve ser maior ou igual a zero.
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Taxa adicional <span class="text-muted">(opcional)</span></label>
                <div class="input-group">
                  <span class="input-group-text">R$</span>
                  <input
                    v-model="form.additional_fee"
                    v-money="moneyConfig"
                    type="text"
                    class="form-control"
                    placeholder="0,00"
                    :class="{ 'is-invalid': submitted && $v.form.additional_fee.$error }"
                    @blur="form.additional_fee = parseFloat(form.additional_fee) || null"
                  />
                </div>
                <div v-if="submitted && !$v.form.additional_fee.minValue" class="invalid-feedback d-block">
                  A taxa deve ser maior ou igual a zero.
                </div>
              </div>
            </div>
            <div class="alert alert-info mb-0">
              <i class="bx bx-info-circle me-2"></i>
              <small>Taxas adicionais são adicionadas ao preço do ingresso e podem ser usadas para cobranças de serviço, taxas de processamento ou impostos.</small>
            </div>
          </div>
        </div>

        <!-- Availability -->
        <div class="ticket-section">
          <div class="section-header">
            <div class="section-icon bg-purple-soft">
              <i class="bx bx-layer text-purple"></i>
            </div>
            <h6 class="mb-0">Disponibilidade</h6>
          </div>

          <div class="section-body">
            <div class="mb-3">
              <label class="form-label">Quantidade disponível <span class="text-danger">*</span></label>
              <input
                v-model.number="form.qtd"
                type="number"
                class="form-control"
                placeholder="100"
                :class="{ 'is-invalid': submitted && $v.form.qtd.$error }"
                @blur="form.qtd = parseInt(form.qtd) || null"
              />
              <small class="text-muted">Número total de ingressos disponíveis para venda</small>
              <div v-if="submitted && !$v.form.qtd.required" class="invalid-feedback d-block">
                O campo quantidade é obrigatório.
              </div>
              <div v-if="submitted && !$v.form.qtd.minValue" class="invalid-feedback d-block">
                A quantidade deve ser maior que zero.
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Mínimo por pedido <span class="text-danger">*</span></label>
                <input
                  v-model.number="form.min_quantity"
                  type="number"
                  class="form-control"
                  placeholder="1"
                  :class="{ 'is-invalid': submitted && $v.form.min_quantity.$error }"
                  @blur="form.min_quantity = parseInt(form.min_quantity) || null"
                />
                <div v-if="submitted && !$v.form.min_quantity.required" class="invalid-feedback">
                  O campo mínimo é obrigatório.
                </div>
                <div v-if="submitted && !$v.form.min_quantity.minValue" class="invalid-feedback">
                  O mínimo deve ser maior que zero.
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Máximo por pedido <span class="text-danger">*</span></label>
                <input
                  v-model.number="form.max_quantity"
                  type="number"
                  class="form-control"
                  placeholder="10"
                  :class="{ 'is-invalid': submitted && $v.form.max_quantity.$error }"
                  @blur="form.max_quantity = parseInt(form.max_quantity) || null"
                />
                <div v-if="submitted && !$v.form.max_quantity.required" class="invalid-feedback">
                  O campo máximo é obrigatório.
                </div>
                <div v-if="submitted && !$v.form.max_quantity.minValue" class="invalid-feedback">
                  O máximo deve ser maior que zero.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sales Period -->
        <div class="ticket-section">
          <div class="section-header">
            <div class="section-icon bg-warning-soft">
              <i class="bx bx-calendar text-warning"></i>
            </div>
            <h6 class="mb-0">Período de vendas</h6>
          </div>

          <div class="section-body">
            <div class="mb-3">
              <label class="form-label">Início das vendas <span class="text-danger">*</span></label>
              <div class="row">
                <div class="col-md-6">
                  <input
                    v-model="form.sale_start_date_only"
                    type="date"
                    class="form-control mb-2 mb-md-0"
                    :class="{ 'is-invalid': submitted && $v.form.sale_start_date_only.$error }"
                  />
                  <div v-if="submitted && !$v.form.sale_start_date_only.required" class="invalid-feedback d-block">
                    A data de início é obrigatória.
                  </div>
                </div>
                <div class="col-md-6">
                  <input
                    v-model="form.sale_start_time"
                    type="time"
                    class="form-control"
                    :class="{ 'is-invalid': submitted && $v.form.sale_start_time.$error }"
                  />
                  <div v-if="submitted && !$v.form.sale_start_time.required" class="invalid-feedback d-block">
                    O horário de início é obrigatório.
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Término das vendas <span class="text-danger">*</span></label>
              <div class="row">
                <div class="col-md-6">
                  <input
                    v-model="form.sale_end_date_only"
                    type="date"
                    class="form-control mb-2 mb-md-0"
                    :class="{ 'is-invalid': submitted && $v.form.sale_end_date_only.$error }"
                  />
                  <div v-if="submitted && !$v.form.sale_end_date_only.required" class="invalid-feedback d-block">
                    A data de término é obrigatória.
                  </div>
                </div>
                <div class="col-md-6">
                  <input
                    v-model="form.sale_end_time"
                    type="time"
                    class="form-control"
                    :class="{ 'is-invalid': submitted && $v.form.sale_end_time.$error }"
                  />
                  <div v-if="submitted && !$v.form.sale_end_time.required" class="invalid-feedback d-block">
                    O horário de término é obrigatório.
                  </div>
                </div>
              </div>
            </div>

            <small class="text-muted">
              <i class="bx bx-time-five me-1"></i>
              Horários mostrados no fuso horário do seu evento (BRT)
            </small>
          </div>
        </div>

        <!-- Display Settings -->
        <div class="ticket-section">
          <div class="section-header">
            <div class="section-icon bg-info-soft">
              <i class="bx bx-show text-info"></i>
            </div>
            <h6 class="mb-0">Exibição na página do evento</h6>
          </div>

          <div class="section-body">
            <p class="text-muted small mb-3">Escolha quais informações serão visíveis para o participante na página de inscrição.</p>

            <div class="d-flex flex-column gap-3">
              <div class="form-check form-switch">
                <input
                  v-model="form.show_available_quantity"
                  class="form-check-input"
                  type="checkbox"
                  id="showAvailableQuantity"
                />
                <label class="form-check-label" for="showAvailableQuantity">
                  Exibir quantidade disponível
                  <small class="d-block text-muted">Ex: "42 disponíveis" ou "Últimas 5 unidades"</small>
                </label>
              </div>

              <div class="form-check form-switch">
                <input
                  v-model="form.show_sales_period"
                  class="form-check-input"
                  type="checkbox"
                  id="showSalesPeriod"
                />
                <label class="form-check-label" for="showSalesPeriod">
                  Exibir período de vendas
                  <small class="d-block text-muted">Ex: "Vendas até 07/05/2026"</small>
                </label>
              </div>

              <div class="form-check form-switch">
                <input
                  v-model="form.show_min_max_per_order"
                  class="form-check-input"
                  type="checkbox"
                  id="showMinMaxPerOrder"
                />
                <label class="form-check-label" for="showMinMaxPerOrder">
                  Exibir limites por pedido
                  <small class="d-block text-muted">Ex: "Máx. 5 por pedido"</small>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="d-flex justify-content-end gap-2 pt-3 border-top">
          <b-button variant="light" @click="showModal = false">
            Cancelar
          </b-button>
          <b-button variant="primary" type="submit" :disabled="loading">
            <i class="bx bx-check me-1" v-if="!loading"></i>
            <b-spinner small v-if="loading"></b-spinner>
            {{ loading ? 'Salvando...' : (isEditing ? 'Salvar alterações' : 'Criar ingresso') }}
          </b-button>
        </div>
      </form>
    </b-modal>
  </Layout>
</template>

<script>
import Swal from 'sweetalert2'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { Money } from 'v-money'
import { getTickets, createTicket, updateTicket, deleteTicket as deleteTicketService, toggleTicketStatus } from './services'

export default {
  name: 'TicketsIndex',
  components: {
    Layout,
    PageHeader
  },
  directives: { money: Money },
  validations: {
    form: {
      name: { required },
      description: {},
      type_ticket: { required },
      amount_ticket: {
        required: requiredIf(function () {
          return this.form.type_ticket === 0
        }),
        minValue: function (value) {
          if (this.form.type_ticket === 0) {
            return value != null && value >= 0
          }
          return true
        }
      },
      qtd: {
        required,
        minValue: function (value) {
          return value != null && value >= 1
        }
      },
      additional_fee: {
        minValue: function (value) {
          if (value == null || value === '') return true
          return value >= 0
        }
      },
      sale_start_date_only: { required },
      sale_start_time: { required },
      sale_end_date_only: { required },
      sale_end_time: { required },
      min_quantity: {
        required,
        minValue: function (value) {
          return value != null && value >= 1
        }
      },
      max_quantity: {
        required,
        minValue: function (value) {
          return value != null && value >= 1
        }
      }
    }
  },
  data () {
    return {
      tickets: [],
      loading: false,
      showModal: false,
      isEditing: false,
      submitted: false,
      search: '',
      title: 'Ingressos',
      items: [
        {
          text: 'Bilheteria',
          to: '/payments/dashboard'
        },
        {
          text: 'Ingressos',
          active: true
        }
      ],
      moneyConfig: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: '',
        precision: 2,
        masked: false
      },
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0
      },
      form: {
        name: '',
        description: '',
        type_ticket: 0, // Pago por padrão
        amount_ticket: null,
        qtd: null,
        qtd_sold: 0,
        additional_fee: null,
        sale_start_date: '',
        sale_end_date: '',
        sale_start_date_only: '',
        sale_start_time: '',
        sale_end_date_only: '',
        sale_end_time: '',
        min_quantity: null,
        max_quantity: null,
        show_available_quantity: false,
        show_sales_period: false,
        show_min_max_per_order: false,
        status: 1
      }
    }
  },
  mounted () {
    this.loadTickets()
  },
  methods: {
    async loadTickets (page = 1) {
      try {
        const params = {
          page,
          limit: this.pagination.per_page,
          search: this.search || undefined
        }
        const response = await getTickets(params)
        this.tickets = response.data.data
        this.pagination = {
          current_page: response.data.current_page,
          per_page: response.data.per_page,
          total: response.data.total
        }
      } catch (error) {
        console.error('Erro ao carregar ingressos:', error)
        this.$toast.error('Erro ao carregar ingressos')
      }
    },
    doSearch () {
      this.pagination.current_page = 1
      this.loadTickets(1)
    },
    openCreateModal () {
      this.isEditing = false
      this.submitted = false
      this.resetForm()
      this.showModal = true
    },
    editTicket (ticket) {
      this.isEditing = true
      this.submitted = false
      this.form = { ...ticket }

      // Garantir que valores numéricos sejam números
      this.form.amount_ticket = parseFloat(this.form.amount_ticket) || null
      this.form.qtd = parseInt(this.form.qtd) || null
      this.form.additional_fee = parseFloat(this.form.additional_fee) || null
      this.form.min_quantity = parseInt(this.form.min_quantity) || null
      this.form.max_quantity = parseInt(this.form.max_quantity) || null

      // Split dates into date and time for separate inputs
      if (this.form.sale_start_date) {
        const startDate = new Date(this.form.sale_start_date)
        this.form.sale_start_date_only = startDate.toISOString().split('T')[0]
        this.form.sale_start_time = startDate.toTimeString().slice(0, 5)
      }
      if (this.form.sale_end_date) {
        const endDate = new Date(this.form.sale_end_date)
        this.form.sale_end_date_only = endDate.toISOString().split('T')[0]
        this.form.sale_end_time = endDate.toTimeString().slice(0, 5)
      }
      this.showModal = true
    },
    async saveTicket () {
      this.submitted = true

      // Validate form
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$toast.error('Por favor, preencha todos os campos obrigatórios')
        return
      }

      try {
        this.loading = true

        // Combine date and time before sending
        const dataToSend = { ...this.form }

        // Garantir que valores numéricos não sejam null
        dataToSend.amount_ticket = parseFloat(dataToSend.amount_ticket) || 0
        dataToSend.qtd = parseInt(dataToSend.qtd) || 0
        dataToSend.additional_fee = parseFloat(dataToSend.additional_fee) || 0
        dataToSend.min_quantity = parseInt(dataToSend.min_quantity) || 1
        dataToSend.max_quantity = parseInt(dataToSend.max_quantity) || 1

        // Garantir que description seja string vazia se não preenchida
        dataToSend.description = dataToSend.description || ''

        if (this.form.sale_start_date_only && this.form.sale_start_time) {
          dataToSend.sale_start_date = `${this.form.sale_start_date_only} ${this.form.sale_start_time}:00`
        }
        if (this.form.sale_end_date_only && this.form.sale_end_time) {
          dataToSend.sale_end_date = `${this.form.sale_end_date_only} ${this.form.sale_end_time}:00`
        }

        // Remove temporary fields
        delete dataToSend.sale_start_date_only
        delete dataToSend.sale_start_time
        delete dataToSend.sale_end_date_only
        delete dataToSend.sale_end_time

        if (this.isEditing) {
          await updateTicket(dataToSend.id, dataToSend)
          this.$toast.success('Ingresso atualizado com sucesso')
        } else {
          await createTicket(dataToSend)
          this.$toast.success('Ingresso criado com sucesso')
        }
        this.showModal = false
        this.submitted = false
        this.loadTickets(this.pagination.current_page)
      } catch (error) {
        console.error('Erro ao salvar ingresso:', error)
        this.$toast.error('Erro ao salvar ingresso')
      } finally {
        this.loading = false
      }
    },
    async toggleStatus (ticket) {
      try {
        await toggleTicketStatus(ticket.id)
        this.$toast.success('Status atualizado com sucesso')
        this.loadTickets(this.pagination.current_page)
      } catch (error) {
        console.error('Erro ao atualizar status:', error)
        this.$toast.error('Erro ao atualizar status')
      }
    },
    deleteTicket (ticket) {
      Swal.fire({
        title: 'Você tem certeza?',
        html: `Será excluído o ingresso: <b>${ticket.name}</b>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'CANCELAR',
        confirmButtonText: 'SIM, DESEJO EXCLUIR!'
      }).then(async (result) => {
        if (result.value) {
          try {
            await deleteTicketService(ticket.id)
            this.$toast.success('Ingresso excluído com sucesso')
            this.loadTickets(this.pagination.current_page)
          } catch (error) {
            console.error('Erro ao excluir ingresso:', error)
            this.$toast.error('Erro ao excluir ingresso')
          }
        }
      })
    },
    resetForm () {
      this.submitted = false
      this.form = {
        name: '',
        description: '',
        type_ticket: 0, // Pago por padrão
        amount_ticket: null,
        qtd: null,
        qtd_sold: 0,
        additional_fee: null,
        sale_start_date: '',
        sale_end_date: '',
        sale_start_date_only: '',
        sale_start_time: '',
        sale_end_date_only: '',
        sale_end_time: '',
        min_quantity: null,
        max_quantity: null,
        show_available_quantity: false,
        show_sales_period: false,
        show_min_max_per_order: false,
        status: 1
      }
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset()
        }
      })
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value || 0)
    },
    formatDate (date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short'
      })
    }
  }
}
</script>

<style scoped>
.link {
  cursor: pointer;
}

.swal2-popup {
  font-size: 11px !important;
}

.table-responsive {
  min-height: 300px;
}

.float-sm-end {
  float: right;
}

.bg-success-subtle {
  background-color: #d1f4e0 !important;
}

.text-success {
  color: #28a745 !important;
}

.bg-danger-subtle {
  background-color: #f8d7da !important;
}

.text-danger {
  color: #dc3545 !important;
}

.bg-info-subtle {
  background-color: #d1ecf1 !important;
}

.text-info {
  color: #0dcaf0 !important;
}

.gap-2 {
  gap: 0.5rem;
}

/* Modal Sections */
.ticket-section {
  padding: 0;
  margin-bottom: 1.5rem;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.section-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 18px;
  margin-right: 0.75rem;
}

.bg-primary-soft {
  background-color: rgba(85, 110, 230, 0.1);
}

.bg-success-soft {
  background-color: rgba(52, 195, 143, 0.1);
}

.bg-purple-soft {
  background-color: rgba(128, 90, 213, 0.1);
}

.text-purple {
  color: #805ad5;
}

.bg-warning-soft {
  background-color: rgba(242, 184, 39, 0.1);
}

.bg-info-soft {
  background-color: rgba(13, 202, 240, 0.1);
}

.section-body {
  padding: 0;
}

/* Ticket Type Toggle */
.ticket-type-toggle {
  border-radius: 6px;
  overflow: hidden;
}

.ticket-type-toggle .btn {
  padding: 0.5rem 1.5rem;
  border-radius: 0;
  font-weight: 500;
  border: 1px solid #dee2e6;
}

.ticket-type-toggle .btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.ticket-type-toggle .btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.ticket-type-toggle .btn.active {
  z-index: 2;
}

.ticket-type-toggle .btn-light {
  background-color: white;
  border-color: #dee2e6;
  color: #495057;
}

.ticket-type-toggle .btn-light.active {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

.ticket-type-toggle .btn-primary.active {
  background-color: #556ee6;
  border-color: #556ee6;
  color: white;
}

.ticket-type-toggle .btn-outline-primary {
  background-color: white;
  border-color: #dee2e6;
  color: #495057;
}

.ticket-type-toggle .btn-outline-light {
  background-color: white;
  border-color: #dee2e6;
  color: #495057;
}

/* Alert Info */
.alert-info {
  background-color: #e7f3ff;
  border-color: #b3d9ff;
  color: #004085;
  border-radius: 6px;
}
</style>
